<template>
  <component
    :is="el"
    class="sc"
    :class="['sc--' + size, 'sc--' + state, 'sc--t-' + kind, { 'is-selected': selected, 'is-selectable': el === 'button', 'sc--calendar': calendar }]"
    :type="el === 'button' ? 'button' : null"
    :aria-pressed="selectable ? (selected ? 'true' : 'false') : null"
    :data-state="state"
    :data-type="kind"
    :style="calendar && color ? { '--sc-poc': color } : null"
    :title="tip"
  >
    <template v-if="calendar">
      <span class="sc__line">
        <VisitTypeIcon :type="kind" :size="14" class="sc__ico" />
        <span class="sc__time" dir="ltr"><span class="sc__a">{{ parts.a }}</span><span v-if="parts.b" class="sc__b"> - {{ parts.b }}</span></span>
        <span v-if="line2" class="sc__who" :class="{ 'sc__who--state': !who }">{{ line2 }}</span>
      </span>
      <span v-if="hasLine3" class="sc__purpose sc__l3">
        <span v-if="purpose" class="sc__pt">{{ purpose }}</span>
        <span v-if="!loc && place" class="sc__pt sc__pl">{{ purpose ? '- ' : '' }}{{ place }}</span>
        <span v-if="loc" class="sc__loc" :style="{ '--sc-loc': loc.colour }" :data-testid="'slot-location'" :title="loc.name">
          <i class="sc__locdot" aria-hidden="true"></i><span class="sc__locn">{{ loc.name }}</span><span class="sc__locs">{{ loc.short }}</span>
        </span>
      </span>
      <span class="sc__state">{{ stateName }}<template v-if="done">, done</template></span>
      <slot />
    </template>
    <template v-else>
    <span class="sc__line">
      <VisitTypeIcon :type="kind" :size="size === 'row' ? 16 : 14" class="sc__ico" />
      <span class="sc__time" dir="ltr"><span class="sc__a">{{ parts.a }}</span><span v-if="parts.b" class="sc__b"> - {{ parts.b }}</span></span>
    </span>
    <span class="sc__type">{{ typeLabel }}</span>
    <span v-if="showPurpose && purpose" class="sc__purpose">{{ purpose }}</span>
    <span v-if="priceLabel" class="sc__price">{{ priceLabel }}</span>
    <span v-if="who" class="sc__who">{{ who }}</span>
    <span class="sc__state">{{ stateName }}<template v-if="done">, done</template></span>
    <slot />
    </template>
  </component>
</template>

<script>
import './slotTokens.css'
import VisitTypeIcon from '@/components/VisitTypeIcon.vue'
import { rangeParts, rangeText, slotTypeKind, slotTypeName, priceText, SLOT_STATE_NAME } from '@/utils/slotCard'


// TB-152: the one slot card. Content and order are fixed: [type icon] start - end,
// type name, purpose (when present), price. Colour follows the state; the icon
// carries the type. Variants change size and selectability only:
//   size 'chip' (compact), 'row' (full width), 'fill' (fills a positioned box, content drops by height)
// TB-155/TB-162: variant 'calendar' is the doctor calendar layout: purpose (POC) colour strip on the start edge
// (type colour when the purpose has none), [type icon] start - end, 'Free' or the patient name, purpose - location; status colour on border and fill, no type name, no price.
export default {
  name: 'SlotCard',
  components: { VisitTypeIcon },
  props: {
    start: { type: String, default: '' },
    end: { type: String, default: '' },
    // consultation type title ("Walk-Ins", "Video Call") or key (clinic, video, home)
    type: { type: String, default: 'clinic' },
    purpose: { type: String, default: '' },
    // amount as stored; shown in the currency from settings
    price: { type: [Number, String], default: 0 },
    state: { type: String, default: 'free' },
    size: { type: String, default: 'chip' },
    variant: { type: String, default: '' },
    selectable: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    tag: { type: String, default: '' },
    who: { type: String, default: '' },
    // TB-162: calendar variant only. color = purpose (POC) colour for the edge strip, place = location abbreviation
    color: { type: String, default: '' },
    place: { type: String, default: '' },
    // TB-169: { name, short, colour } of the slot location; shown as a tint pill (name when wide, short name when narrow)
    location: { type: Object, default: null },
    // TB-162: patients never see the purpose; only doctor and clinic screens pass this on the default variant
    showPurpose: { type: Boolean, default: false },
    done: { type: Boolean, default: false }
  },
  computed: {
    calendar () { return this.variant === 'calendar' },
    line2 () { return this.who || (this.state === 'free' || this.state === 'past' ? 'Free' : this.stateName) },
    loc () { return this.location && (this.location.name || this.location.short) ? { name: this.location.name || this.location.short, short: this.location.short || this.location.name, colour: this.location.colour || '#3398db' } : null },
    hasLine3 () { return !!(this.purpose || this.place || this.loc) },
    kind () { return slotTypeKind(this.type) },
    typeLabel () { return slotTypeName(this.type) },
    range () { return rangeText(this.start, this.end) },
    parts () { return rangeParts(this.start, this.end) },
    priceLabel () { return priceText(this.price) },
    stateName () { return SLOT_STATE_NAME[this.state] || '' },
    tip () { return [this.range, this.typeLabel, this.calendar || this.showPurpose ? this.purpose : '', this.loc ? this.loc.name : this.place, this.priceLabel, this.stateName].filter(Boolean).join(' · ') },
    el () { return this.tag || (this.selectable ? 'button' : 'div') }
  }
}
</script>

<style>
/* TB-152/TB-155 palette: slot state colours are the schedule tokens (--sl-*, slot/slotTokens.css),
   the same as the month view bars and the legend swatches. The type is the icon, or the edge strip. */
:root {
  --sc-t-clinic: #006aa6; --sc-t-video: #0b7fb8; --sc-t-home: #c01860;
  --sc-free-fg: var(--sl-free-fg); --sc-free-bg: var(--sl-free-bg); --sc-free-line: var(--sl-free-line);
  --sc-held-fg: #8a5a00; --sc-held-bg: #fff6e0; --sc-held-line: #d9971a;
  --sc-booked-fg: #ffffff; --sc-booked-bg: var(--sl-booked-bg); --sc-booked-line: var(--sl-booked-bg);
  --sc-cancel-fg: var(--sl-cancel-fg); --sc-cancel-bg: color-mix(in srgb, var(--sl-cancel-fg) 8%, transparent); --sc-cancel-line: var(--sl-cancel-fg);
  --sc-past-fg: var(--sl-done-fg); --sc-past-bg: var(--sl-done-bg); --sc-past-line: var(--sl-done-fg);
  --sc-blocked-fg: var(--sl-blocked-fg); --sc-blocked-line: var(--sl-blocked-line);
  --sc-hatch: var(--sl-hatch);
}
:root[data-theme="dark"] {
  --sc-t-clinic: #8fd0f5; --sc-t-video: #7cc8f0; --sc-t-home: #f6a9c9;
  --sc-held-fg: #f2c46b; --sc-held-bg: rgba(217, 151, 26, 0.16); --sc-held-line: #b8801a;
}

.sc {
  --sc-fg: var(--sc-free-fg); --sc-bg: var(--sc-free-bg); --sc-line: var(--sc-free-line);
  display: flex; flex-direction: column; gap: 1px; min-width: 0; box-sizing: border-box;
  margin: 0; padding: 0.4rem 0.6rem; border-radius: var(--tb-radius-control, 10px);
  border: 1.5px solid var(--sc-line); background: var(--sc-bg); color: var(--sc-fg);
  font: inherit; font-size: 0.8rem; line-height: 1.25; text-align: start; text-decoration: none;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
button.sc { cursor: pointer; }
.sc--free { --sc-fg: var(--sc-free-fg); --sc-bg: var(--sc-free-bg); --sc-line: var(--sc-free-line); }
.sc--held { --sc-fg: var(--sc-held-fg); --sc-bg: var(--sc-held-bg); --sc-line: var(--sc-held-line); border-style: dashed; }
.sc--booked { --sc-fg: var(--sc-booked-fg); --sc-bg: var(--sc-booked-bg); --sc-line: var(--sc-booked-line); }
.sc--t-clinic { --sc-type: var(--sc-t-clinic); }
.sc--t-video { --sc-type: var(--sc-t-video); }
.sc--t-home { --sc-type: var(--sc-t-home); }
/* Waiting for payment keeps the consultation type colour; only the dashed border tells it is pending. */
.sc--pending { --sc-fg: var(--sc-type); --sc-bg: color-mix(in srgb, var(--sc-type) 10%, transparent); --sc-line: var(--sc-type); border-style: dashed; }
.sc--cancelled { --sc-fg: var(--sc-cancel-fg); --sc-bg: var(--sc-cancel-bg); --sc-line: var(--sc-cancel-line); }
.sc--cancelled .sc__time { text-decoration: line-through; }
.sc--past { --sc-fg: var(--sc-past-fg); --sc-bg: var(--sc-past-bg); --sc-line: var(--sc-past-line); }
.sc--blocked { --sc-fg: var(--sc-blocked-fg); --sc-bg: transparent; --sc-line: var(--sc-blocked-line); background-image: var(--sc-hatch); }
button.sc:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--tb-shadow-sm); }
.sc--past:hover { transform: none; }
.sc:focus-visible { outline: none; box-shadow: var(--tb-shadow-focus); }
.sc.is-selected, button.sc.is-selected:hover:not(:disabled) { box-shadow: 0 0 0 3px var(--tb-primary); border-style: solid; border-color: var(--tb-primary); }

.sc__line { display: flex; align-items: center; gap: 0.35rem; min-width: 0; }
.sc__ico { color: var(--sc-t-clinic); flex: none; }
.sc--t-video .sc__ico { color: var(--sc-t-video); }
.sc--t-home .sc__ico { color: var(--sc-t-home); }
.sc--booked .sc__ico { color: #fff; }
.sc__time { font-weight: 800; font-variant-numeric: tabular-nums; unicode-bidi: isolate; direction: ltr; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc__type, .sc__purpose, .sc__price, .sc__who { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.sc__type { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.sc__purpose { opacity: 0.9; }
.sc__price { font-weight: 700; font-variant-numeric: tabular-nums; }
.sc__who { font-weight: 700; text-transform: capitalize; }
.sc__state { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

.sc--chip { font-size: 0.78rem; padding: 0.35rem 0.55rem; }
.sc--chip .sc__time { font-size: 0.76rem; }
.sc--row { width: 100%; font-size: 0.9rem; padding: 0.6rem 0.85rem; gap: 2px; }
.sc--row .sc__time { font-size: 1rem; }
.sc--fill { position: relative; width: 100%; height: 100%; overflow: hidden; padding: 3px 7px; gap: 0; container: sc / size; font-size: 0.74rem; }
/* calendar variant (TB-162): real left border in the purpose colour (type colour when the purpose has none) */
.sc--calendar { gap: 2px; }
.sc.sc--calendar, button.sc.sc--calendar.is-selected:hover:not(:disabled) { border-inline-start: 4px solid var(--sc-poc, var(--sc-type, var(--sc-t-clinic))) !important; }
.sc--calendar.sc--fill { padding: 3px 7px; gap: 0; }
.sc--calendar .sc__line { flex-wrap: wrap; row-gap: 0; column-gap: 0.35rem; }
.sc--calendar .sc__ico { align-self: center; }
.sc--calendar .sc__time { flex: none; }
.sc--calendar .sc__who { flex: 1 1 3.5rem; min-width: 0; }
.sc--calendar .sc__who:not(.sc__who--state) { font-weight: 700; }
.sc__who--state { font-weight: 600; text-transform: none; }
.sc--calendar .sc__who::before { content: '\00b7'; margin-inline-end: 0.35rem; font-weight: 800; }
.sc--calendar .sc__purpose { opacity: 0.9; }
/* TB-169: line 3 is "purpose" plus a location pill: 16% tint of the location colour behind the card's own text colour, solid dot in the full colour */
.sc__l3 { display: flex; align-items: center; gap: 0.35rem; opacity: 1; }
.sc--calendar .sc__l3 { opacity: 1; }
.sc__pt { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; opacity: 0.92; flex: 0 1 auto; }
.sc__pl { flex: 0 0 auto; }
.sc__loc { display: inline-flex; align-items: center; gap: 0.3rem; flex: 0 1 auto; min-width: 0; max-width: 100%; padding: 0 0.4rem; border-radius: 999px; font-size: 0.92em; font-weight: 700; line-height: 1.5; color: inherit; background: color-mix(in srgb, var(--sc-loc) 16%, transparent); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--sc-loc) 30%, transparent); overflow: hidden; }
.sc__locdot { flex: none; width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--sc-loc); box-shadow: 0 0 0 1px color-mix(in srgb, #fff 55%, transparent); }
.sc__locn, .sc__locs { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.sc__locs { display: none; }
@container sc (max-width: 165px) { .sc__locn { display: none; } .sc__locs { display: inline; } }
@container scw (max-width: 200px) { .sc__locn { display: none; } .sc__locs { display: inline; } }
.sc--calendar.sc--row { container: scw / inline-size; }
@container sc (max-width: 190px) {
  .sc--calendar .sc__who { flex-basis: 100%; }
  .sc--calendar .sc__who::before { display: none; }
}
@container scw (max-width: 240px) {
  .sc--calendar .sc__who { flex-basis: 100%; }
  .sc--calendar .sc__who::before { display: none; }
}
@container sc (max-width: 132px) { .sc__b { display: none; } }
@container sc (max-height: 78px) { .sc:not(.sc--calendar) .sc__who { display: none; } }
@container sc (max-height: 64px) { .sc__price { display: none; } }
@container sc (max-height: 50px) { .sc:not(.sc--calendar) .sc__purpose { display: none; } }
@container sc (max-height: 36px) { .sc__type { display: none; } }
@container sc (max-height: 34px) { .sc--calendar .sc__l3 { display: none; } }
@container sc (max-width: 190px) and (max-height: 62px) { .sc--calendar .sc__l3 { display: none; } }
@container sc (max-height: 24px) { .sc--calendar .sc__who { display: none; } }
@media (prefers-reduced-motion: reduce) { .sc { transition: none; } button.sc:hover:not(:disabled) { transform: none; } }
</style>
